import { MdInfoOutline } from 'react-icons/md'
import {
  chakra,
  useDisclosure,
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  CloseButton,
  Flex,
  Text,
} from '@ritualco/jutsu'
import { useContent } from 'lib/content/react'
import { OrganizationType } from 'lib/generated/api'
import { useHistory, useParams } from 'lib/router'
import { IncentivePlanStatus } from 'types/entities'

export type AlertBannerProps = {
  organizationType: OrganizationType
  isMissingPeople: Boolean
  hasChangesDrafted: boolean
  isDraft: boolean
}
export const AlertBanner = ({
  organizationType,
  isMissingPeople,
  hasChangesDrafted,
  isDraft,
}: AlertBannerProps) => {
  const history = useHistory()
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const { formatMessage } = useContent()
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true })

  if (!isOpen) return null

  if (isMissingPeople) {
    if (organizationType === 'COMPANY') {
      return (
        <Alert
          status="info"
          variant="subtle"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center">
            <AlertIcon as={MdInfoOutline} boxSize={7} />
            <AlertDescription>
              {formatMessage({ id: 'pmportal.incentives.noEmployeesAlert' })}
            </AlertDescription>
          </Flex>
          <Button
            variant="link"
            onClick={() => {
              history.push(`/workspaces/${workspaceId}/employees`)
            }}
          >
            {formatMessage({ id: 'pmportal.incentives.inviteEmployees' })}
          </Button>
        </Alert>
      )
    }
  }

  if (hasChangesDrafted) {
    return (
      <Alert
        status="info"
        variant="subtle"
        alignItems="center"
        justifyContent="space-between"
      >
        <Flex alignItems="center">
          <AlertIcon as={MdInfoOutline} boxSize={7} />
          <AlertDescription>
            {formatMessage({ id: 'pmportal.incentives.draftedChangesAlert' })}
          </AlertDescription>
        </Flex>
        <CloseButton onClick={onClose} />
      </Alert>
    )
  }

  if (isDraft) {
    return (
      <Alert
        status="info"
        variant="subtle"
        alignItems="center"
        justifyContent="space-between"
      >
        <Flex alignItems="center">
          <AlertIcon as={MdInfoOutline} boxSize={7} />
          <AlertDescription>
            {formatMessage({ id: 'pmportal.incentives.draftAlert' })}
          </AlertDescription>
        </Flex>
        <CloseButton onClick={onClose} />
      </Alert>
    )
  }
  return null
}

export const Subheading = ({ status }: { status?: IncentivePlanStatus }) => {
  const { formatMessage } = useContent()
  if (!status) return null
  switch (status) {
    case 'draft':
      return (
        <Text color="grayscale.secondary" fontSize="small">
          {formatMessage({ id: 'pmportal.incentives.draftStatus' })}
          {' • '}
          {formatMessage({ id: 'pmportal.incentives.draftDescription' })}
        </Text>
      )
    case 'upcoming':
      return (
        <Text color="grayscale.secondary" fontSize="small">
          <chakra.span color="primary.normal">
            {formatMessage({ id: 'pmportal.incentives.upcomingStatus' })}
          </chakra.span>
          {' • '}
          {formatMessage({ id: 'pmportal.incentives.upcomingDescription' })}
        </Text>
      )
    case 'live':
      return (
        <Text color="grayscale.secondary" fontSize="small">
          <chakra.span color="primary.normal">
            {formatMessage({ id: 'pmportal.incentives.liveStatusAlt' })}
          </chakra.span>
          {' • '}
          {formatMessage({ id: 'pmportal.incentives.launchedDescription' })}
        </Text>
      )
    case 'paused':
      return (
        <Text color="grayscale.secondary" fontSize="small">
          <chakra.span color="yellow.darker">
            {formatMessage({ id: 'pmportal.incentives.pausedStatus' })}
          </chakra.span>
          {' • '}
          {formatMessage({ id: 'pmportal.incentives.launchedDescription' })}
        </Text>
      )
    case 'ended':
      return (
        <Text color="grayscale.secondary" fontSize="small">
          {formatMessage({ id: 'pmportal.incentives.endedStatus' })}
          {' • '}
          {formatMessage({ id: 'pmportal.incentives.endedDescription' })}
        </Text>
      )
  }
}
